import React, { useRef } from 'react';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import VariableChips from './VariableChips';

/**
 * Campos de um modelo: ligado, assunto, pré-cabeçalho, texto e botão (ou texto simples).
 * Etapa e regra de escolha da variante ficam no código: não se editam aqui.
 * @param {{ form: Object, onChange: Function, targets: Array, variables: Array, blocks: Array }} props
 *   onChange(patch) com os campos alterados
 */
function TemplateFields({ form, onChange, targets, variables, blocks }) {
  const refs = { subject: useRef(null), preheader: useRef(null), body: useRef(null) };
  const lastField = useRef('body');

  // Variáveis entram no último campo usado; blocos só no texto
  const insertToken = (token, onlyBody = false) => {
    const field = onlyBody ? 'body' : lastField.current;
    const input = refs[field].current;
    const value = form[field] || '';
    const start = input?.selectionStart ?? value.length;
    const end = input?.selectionEnd ?? value.length;
    onChange({ [field]: value.slice(0, start) + token + value.slice(end) });
    requestAnimationFrame(() => {
      if (!input) return;
      input.focus();
      input.setSelectionRange(start + token.length, start + token.length);
    });
  };

  return (
    <Box sx={{ display: 'grid', gap: 2 }}>
      <FormControlLabel
        control={<Switch checked={form.enabled} onChange={(e) => onChange({ enabled: e.target.checked })} />}
        label={form.enabled ? 'Ligado' : 'Desligado'}
      />

      <TextField
        size="small"
        label="Assunto"
        value={form.subject}
        inputRef={refs.subject}
        onFocus={() => { lastField.current = 'subject'; }}
        onChange={(e) => onChange({ subject: e.target.value })}
        inputProps={{ maxLength: 200 }}
      />

      <TextField
        size="small"
        label="Pré-cabeçalho"
        value={form.preheader}
        inputRef={refs.preheader}
        onFocus={() => { lastField.current = 'preheader'; }}
        onChange={(e) => onChange({ preheader: e.target.value })}
        helperText="Aparece ao lado do assunto na caixa de entrada."
        inputProps={{ maxLength: 200 }}
      />

      <TextField
        multiline
        minRows={8}
        label="Texto"
        value={form.body}
        inputRef={refs.body}
        onFocus={() => { lastField.current = 'body'; }}
        onChange={(e) => onChange({ body: e.target.value })}
        helperText="Linha em branco separa parágrafos. A assinatura entra sozinha."
        inputProps={{ maxLength: 5000 }}
      />

      <VariableChips variables={variables} onInsert={(token) => insertToken(token)} />

      <VariableChips
        title="Blocos ilustrados (cada um sozinho no parágrafo)"
        variables={blocks}
        tokenOf={(key) => key}
        onInsert={(token) => insertToken(token, true)}
      />

      <Box>
        <FormControlLabel
          control={<Switch checked={form.plain} onChange={(e) => onChange({ plain: e.target.checked })} />}
          label="Texto simples, sem botão"
        />
        <Typography variant="caption" color="text.secondary" component="div">
          Parece uma mensagem pessoal. Bom para perguntar se a pessoa precisa de ajuda.
        </Typography>
      </Box>

      {!form.plain && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            label="Texto do botão"
            value={form.button_label}
            onChange={(e) => onChange({ button_label: e.target.value })}
            inputProps={{ maxLength: 80 }}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            select
            size="small"
            label="Leva para"
            value={form.button_target}
            onChange={(e) => onChange({ button_target: e.target.value })}
            sx={{ flex: 1, minWidth: 200 }}
          >
            <MenuItem value=""><em>Sem botão</em></MenuItem>
            {targets.map((item) => <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>)}
          </TextField>
        </Box>
      )}
    </Box>
  );
}

export default TemplateFields;
